import { Controller } from 'react-hook-form';

import { ToggleGroup, ToggleItem } from '@/components/ui/toggle-group';
import type { DashboardParams } from '@/hooks/JobBoardFilters/useJobBoardParams';
import {
  SeniorityLevel,
  useJobBoardParams,
} from '@/hooks/JobBoardFilters/useJobBoardParams';
import useForm from '@/hooks/useForm';
import { industryValues } from '@/services/resume-checker';
import { capitalizeFirstLetter } from '@/utils/cvGenerator';

import ControlButtons from './ControlButtons';
import FilterSection from './FilterSection';

export const INDUSTRY_FORM_ID = 'jb-filter-industry';

export type IndustryFormValues = {
  industry: Exclude<DashboardParams['industry'], undefined> | '';
  seniorityLevel: SeniorityLevel[];
};

const Industries = ({
  defaultValues,
}: {
  defaultValues: IndustryFormValues;
}) => {
  const { setParams } = useJobBoardParams();

  const { control, handleSubmit, reset } = useForm<IndustryFormValues>({
    defaultValues,
  });

  return (
    <form
      id={INDUSTRY_FORM_ID}
      className="flex flex-col justify-center gap-6"
      onSubmit={handleSubmit((data) =>
        setParams({
          industry: data.industry || undefined,
          seniorityLevel: data.seniorityLevel || undefined,
        })
      )}
      onReset={(event) => {
        event.preventDefault();
        reset({ industry: '', seniorityLevel: [] });
      }}
    >
      <div className="flex flex-col divide-y-2 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0 [&>*]:py-3">
        <FilterSection title="Industry" subtitle="Choose an industry">
          <Controller
            control={control}
            name="industry"
            render={({ field: { onChange, value } }) => (
              <ToggleGroup
                type="single"
                variant="jbChip"
                onValueChange={onChange}
                value={value}
              >
                {industryValues.map((industryValue) => (
                  <ToggleItem key={industryValue} value={industryValue}>
                    {industryValue}
                  </ToggleItem>
                ))}
              </ToggleGroup>
            )}
          />
        </FilterSection>
        <FilterSection
          title="Experience Level"
          subtitle="Filter jobs by seniority"
        >
          <Controller
            control={control}
            name="seniorityLevel"
            render={({ field: { onChange, value } }) => (
              <ToggleGroup
                type="multiple"
                variant="jbChip"
                onValueChange={onChange}
                value={value}
              >
                {Object.values(SeniorityLevel).map((seniorityLevel) => (
                  <ToggleItem key={seniorityLevel} value={seniorityLevel}>
                    {capitalizeFirstLetter(seniorityLevel)}
                  </ToggleItem>
                ))}
              </ToggleGroup>
            )}
          />
        </FilterSection>
      </div>
      <ControlButtons />
    </form>
  );
};

export default Industries;
